import { useState, useEffect } from 'react';
import { X, User, Search, CheckCircle, UserPlus, Calendar, Users, ShoppingCart, CreditCard, FileText, Eye, Download, Printer, DollarSign, Plus, Minus, Package } from 'lucide-react';
import { Room, Guest, CreateGuest, CreatePayment, InventoryItem } from '@/shared/types';

interface ReservationWithDetails {
  id: number;
  guest_name: string;
  room_number: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string;
  check_out_time?: string;
  adults: number;
  children: number;
  daily_rate: number;
  total_amount: number;
  status: string;
  payment_status: string;
  notes?: string;
  created_by_name?: string;
  updated_by_name?: string;
}

import DocumentActions from './DocumentActions';
import { apiGet, apiPost } from '@/react-app/utils/api';
interface RoomModalProps {
  room: Room;
  isOpen: boolean;
  onClose: () => void;
  isOccupied: boolean;
  currentReservation?: ReservationWithDetails;
  onRefresh?: () => void;
}

interface ConsumptionItem {
  item_id?: number;
  item_name?: string;
  quantity: number;
  unit_price: number;
  name: string;
  category: string;
  is_custom?: boolean;
}

export default function RoomModal({ room, isOpen, onClose, isOccupied, currentReservation, onRefresh }: RoomModalProps) {
  const [activeTab, setActiveTab] = useState<'guest' | 'hospedagem' | 'consumption' | 'documents' | 'payments'>('guest');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selectedGuestId, setSelectedGuestId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewGuestForm, setShowNewGuestForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [consumptionItems, setConsumptionItems] = useState<ConsumptionItem[]>([]);
  const [consumptionSearch, setConsumptionSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItem, setCustomItem] = useState({ name: '', price: 0 });
  const [dailyRate, setDailyRate] = useState(200); // Default daily rate

  const [newGuest, setNewGuest] = useState<CreateGuest>({
    name: '',
    document: '',
    phone: '',
    email: '',
    address: '',
    guest_type: 'individual',
    company_name: '',
    cnpj: '',
    company_email: '',
    company_phone: ''
  });

  const [reservationData, setReservationData] = useState({
    check_in_date: new Date().toISOString().split('T')[0],
    check_out_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    adults: 1,
    children: 0,
    discount: 0,
    notes: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'cash' as 'cash' | 'credit_card' | 'debit_card' | 'pix' | 'courtesy' | 'billing',
    notes: ''
  });

  // New states for checkout date change and room reallocation
  const [showCheckoutDateForm, setShowCheckoutDateForm] = useState(false);
  const [newCheckoutDate, setNewCheckoutDate] = useState('');
  const [showRoomReallocationForm, setShowRoomReallocationForm] = useState(false);
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [selectedNewRoomId, setSelectedNewRoomId] = useState<number | null>(null);

  const [existingConsumptions, setExistingConsumptions] = useState<any[]>([]);
  const [existingPayments, setExistingPayments] = useState<any[]>([]);
  const [reservationTotal, setReservationTotal] = useState<any>(null);
  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(0);

  useEffect(() => {
    if (isOpen && !isOccupied) {
      fetchGuests();
    }
    
    if (isOpen && isOccupied) {
      setActiveTab('hospedagem');
      fetchInventoryItems();
      if (currentReservation) {
        fetchExistingConsumptions(currentReservation.id);
        fetchExistingPayments(currentReservation.id);
        fetchReservationTotal(currentReservation.id);
      }
    }
  }, [isOpen, isOccupied, currentReservation]);

  // Simple daily rate setup
  useEffect(() => {
    setReservationData(prev => ({ ...prev, daily_rate: dailyRate }));
  }, [dailyRate]);

  const fetchGuests = async () => {
    try {
      const response = await fetch('/api/guests');
      const data = await response.json();
      setGuests(data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await apiGet('/api/inventory');
      const data = await response.json();
      setInventoryItems(data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchExistingConsumptions = async (reservationId: number) => {
    try {
      const response = await apiGet(`/api/guest-consumptions?reservation_id=${reservationId}`);
      const data = await response.json();
      setExistingConsumptions(data);
    } catch (error) {
      console.error('Error fetching consumptions:', error);
    }
  };

  const fetchExistingPayments = async (reservationId: number) => {
    try {
      const response = await apiGet(`/api/payments?reservation_id=${reservationId}`);
      const data = await response.json();
      setExistingPayments(data);
      
      // Calcular total pago
      const total = data.reduce((sum: number, payment: any) => sum + payment.amount, 0);
      setTotalPaid(total);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchReservationTotal = async (reservationId: number) => {
    try {
      const response = await apiGet(`/api/reservations/${reservationId}/total`);
      const data = await response.json();
      setReservationTotal(data);
    } catch (error) {
      console.error('Error fetching reservation total:', error);
    }
  };

  // Recalcular saldo restante quando totais mudarem
  useEffect(() => {
    if (reservationTotal && reservationTotal.total_amount) {
      const remaining = reservationTotal.total_amount - totalPaid;
      setRemainingBalance(Math.max(0, remaining));
    }
  }, [reservationTotal, totalPaid]);

  // Cálculo automático de preços
  const priceBreakdown = {
    baseRate: room?.daily_rate || 130,
    additionalAdults: Math.max(0, reservationData.adults - 1),
    children: reservationData.children,
    discount: reservationData.discount,
    get finalRate() {
      const base = this.baseRate;
      const adultsExtra = this.additionalAdults * 40;
      const childrenExtra = this.children * 20;
      const subtotal = base + adultsExtra + childrenExtra;
      return Math.max(0, subtotal - this.discount);
    }
  };

  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Preparar dados do hóspede baseado no tipo
      const guestData = {
        ...newGuest,
        // Se for pessoa jurídica e não tiver nome de contato, usar razão social
        name: newGuest.guest_type === 'corporate' && !newGuest.name ? newGuest.company_name : newGuest.name
      };

      const response = await fetch('/api/guests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(guestData),
      });

      if (response.ok) {
        const guest = await response.json();
        setSelectedGuestId(guest.id);
        setShowNewGuestForm(false);
        setNewGuest({ 
          name: '', document: '', phone: '', email: '', address: '',
          guest_type: 'individual', company_name: '', cnpj: '', company_email: '', company_phone: '' 
        });
        await fetchGuests();
      } else {
        const errorData = await response.json();
        console.error('Error creating guest:', errorData);
        alert('Erro ao criar hóspede: ' + (errorData.error || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Error creating guest:', error);
      alert('Erro ao criar hóspede. Verifique sua conexão e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateReservation = async () => {
    if (!selectedGuestId) return;
    setIsLoading(true);

    try {
      const response = await fetch('/api/reservations', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        body: JSON.stringify({
          guest_id: selectedGuestId,
          room_id: room.id,
          ...reservationData,
          daily_rate: priceBreakdown.finalRate,
          total_amount: priceBreakdown.finalRate
        }),
        credentials: 'include'
      });

      if (response.ok) {
        onRefresh?.();
        onClose();
        setSelectedGuestId(null);
        setReservationData({
          check_in_date: new Date().toISOString().split('T')[0],
          check_out_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          adults: 1,
          children: 0,
          discount: 0,
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to change checkout date
  const handleChangeCheckoutDate = async () => {
    if (!currentReservation || !newCheckoutDate) return;
    setIsLoading(true);

    try {
      // Calculate new number of nights
      const checkInDate = new Date(currentReservation.check_in_date);
      const newCheckOutDate = new Date(newCheckoutDate);
      const timeDiff = newCheckOutDate.getTime() - checkInDate.getTime();
      const newNights = Math.max(1, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
      
      // Calculate new total amount based on occupancy
      const adults = currentReservation.adults || 1;
      const children = currentReservation.children || 0;
      
      let dailyRate = 130.00; // Solteiro
      if (adults === 2) {
        dailyRate = 170.00; // Casal
      } else if (adults >= 3) {
        dailyRate = 210.00; // Triplo
      }
      
      // Add children cost
      const childrenCost = children * 20.00;
      const totalDailyRate = dailyRate + childrenCost;
      const newTotalAmount = totalDailyRate * newNights;

      const response = await fetch(`/api/reservations/${currentReservation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          check_out_date: newCheckoutDate,
          total_amount: newTotalAmount
        }),
      });

      if (response.ok) {
        alert(`Data de check-out alterada com sucesso! Novo total: R$ ${newTotalAmount.toFixed(2)} (${newNights} diárias)`);
        setShowCheckoutDateForm(false);
        setNewCheckoutDate('');
        
        // Force reload all data
        if (currentReservation) {
          await fetchReservationTotal(currentReservation.id);
          await fetchExistingConsumptions(currentReservation.id);
          await fetchExistingPayments(currentReservation.id);
        }
        
        // Dados já foram recarregados - não precisa de refresh
      }
    } catch (error) {
      console.error('Error changing checkout date:', error);
      alert('Erro ao alterar data de check-out');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch available rooms for reallocation
  const fetchAvailableRooms = async () => {
    if (!currentReservation) return;

    try {
      const response = await fetch('/api/rooms', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const allRooms = await response.json();
        // Filter out current room and unavailable rooms
        const available = allRooms.filter((room: Room) => 
          room.id !== currentReservation.id && 
          (room.status === 'available' || room.status === 'cleaning')
        );
        setAvailableRooms(available);
      }
    } catch (error) {
      console.error('Error fetching available rooms:', error);
    }
  };

  // Function to reallocate guest to another room
  const handleRoomReallocation = async () => {
    if (!currentReservation || !selectedNewRoomId) return;
    setIsLoading(true);

    try {
      // Update reservation with new room
      const response = await fetch(`/api/reservations/${currentReservation.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          room_id: selectedNewRoomId 
        }),
      });

      if (response.ok) {
        // Update old room status to available  
        await fetch(`/api/rooms/${room.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ status: 'available' }),
        });

        // Update new room status to occupied
        await fetch(`/api/rooms/${selectedNewRoomId}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ status: 'occupied' }),
        });

        alert('Hóspede realocado com sucesso!');
        setShowRoomReallocationForm(false);
        setSelectedNewRoomId(null);
        onRefresh?.();
        onClose();
      }
    } catch (error) {
      console.error('Error reallocating guest:', error);
      alert('Erro ao realocar hóspede');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!currentReservation) return;
    
    // Verificar se o pagamento está completo
    if (remainingBalance > 0) {
      alert(`Não é possível fazer check-out. Ainda falta pagar R$ ${remainingBalance.toFixed(2)}`);
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch(`/api/reservations/${currentReservation.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        body: JSON.stringify({ status: 'checked_out' }),
        credentials: 'include'
      });

      if (response.ok) {
        // Update room status to maintenance after checkout
        await fetch(`/api/rooms/${room.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ status: 'maintenance' }),
        });

        alert('Check-out realizado com sucesso! Quarto em manutenção.');
        onRefresh?.();
        onClose();
      }
    } catch (error) {
      console.error('Error checking out:', error);
      alert('Erro ao realizar check-out');
    } finally {
      setIsLoading(false);
    }
  };

  const addConsumptionItem = (item: InventoryItem) => {
    setConsumptionItems(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) {
        return prev.map(c => 
          c.item_id === item.id 
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, { 
        item_id: item.id, 
        quantity: 1, 
        unit_price: item.sale_price,
        name: item.name,
        category: item.category,
        is_custom: false
      }];
    });
  };

  const addCustomItem = () => {
    if (!customItem.name || customItem.price <= 0) return;
    
    setConsumptionItems(prev => [...prev, {
      item_name: customItem.name,
      quantity: 1,
      unit_price: customItem.price,
      name: customItem.name,
      category: 'Adicional',
      is_custom: true
    }]);
    
    setCustomItem({ name: '', price: 0 });
    setShowCustomItemForm(false);
  };

  const updateConsumptionQuantity = (itemId: number | string, quantity: number) => {
    if (quantity <= 0) {
      setConsumptionItems(prev => prev.filter(c => 
        typeof itemId === 'number' ? c.item_id !== itemId : c.item_name !== itemId
      ));
    } else {
      setConsumptionItems(prev => 
        prev.map(c => {
          const matches = typeof itemId === 'number' 
            ? c.item_id === itemId 
            : c.item_name === itemId;
          return matches ? { ...c, quantity } : c;
        })
      );
    }
  };

  const removeConsumptionItem = (itemId: number | string) => {
    setConsumptionItems(prev => prev.filter(c => 
      typeof itemId === 'number' ? c.item_id !== itemId : c.item_name !== itemId
    ));
  };

  const clearConsumptionCart = () => {
    setConsumptionItems([]);
  };

  const handleSaveConsumption = async () => {
    if (!currentReservation || consumptionItems.length === 0) return;
    
    console.log('Salvando consumos:', consumptionItems);
    setIsLoading(true);

    try {
      for (const item of consumptionItems) {
        const payload = {
          reservation_id: currentReservation.id,
          item_id: item.item_id || null,
          item_name: item.item_name || item.name || null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_amount: item.quantity * item.unit_price
        };
        
        console.log('Enviando item:', payload);
        const response = await apiPost('/api/guest-consumptions', payload);
        console.log('Resposta item:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Erro ao salvar item:', errorText);
          throw new Error(`Erro ao salvar ${item.item_name}: ${errorText}`);
        }
      }
      
      setConsumptionItems([]);
      
      // Recarregar dados para atualizar totais
      console.log('Recarregando dados...');
      await fetchExistingConsumptions(currentReservation.id);
      await fetchExistingPayments(currentReservation.id);
      await fetchReservationTotal(currentReservation.id);
      
      // Não fazer refresh automático - dados já foram recarregados
      
      alert('Consumos registrados com sucesso!');
    } catch (error) {
      console.error('Error saving consumption:', error);
      alert(`Erro ao salvar consumos: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentReservation) return;
    
    console.log('Iniciando pagamento:', paymentData);
    setIsLoading(true);

    try {
      // Se for cortesia, zerar a diária da reserva
      if (paymentData.payment_method === 'courtesy') {
        console.log('Aplicando cortesia - zerando diária');
        const courtesyResponse = await apiPost(`/api/reservations/${currentReservation.id}`, {
          daily_rate: 0
        });
        console.log('Resposta cortesia:', courtesyResponse.status);
      }

      const paymentPayload = {
        ...paymentData,
        reservation_id: currentReservation.id
      };
      
      console.log('Enviando pagamento:', paymentPayload);
      const response = await apiPost('/api/payments', paymentPayload);
      console.log('Resposta pagamento:', response.status);

      if (response.ok) {
        setShowPaymentForm(false);
        setPaymentData({ amount: 0, payment_method: 'cash', notes: '' });
        
        // Recarregar dados para atualizar totais
        console.log('Recarregando dados...');
        await fetchExistingConsumptions(currentReservation.id);
        await fetchExistingPayments(currentReservation.id);
        await fetchReservationTotal(currentReservation.id);
        
        // Não fazer refresh automático - dados já foram recarregados
        
        alert('Pagamento registrado com sucesso!');
      } else {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        alert(`Erro ao registrar pagamento: ${errorText}`);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(`Erro ao processar pagamento: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConsumption = async (consumptionId: number) => {
    if (!confirm('Tem certeza que deseja excluir este consumo?')) return;
    
    try {
      const response = await fetch(`/api/guest-consumptions/${consumptionId}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Recarregar dados
        if (currentReservation) {
          await fetchExistingConsumptions(currentReservation.id);
          await fetchReservationTotal(currentReservation.id);
        }
        alert('Consumo excluído com sucesso!');
      } else {
        const errorText = await response.text();
        alert(`Erro ao excluir consumo: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting consumption:', error);
      alert('Erro ao excluir consumo');
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!confirm('Tem certeza que deseja excluir este pagamento?')) return;
    
    try {
      const response = await fetch(`/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        credentials: 'include'
      });

      if (response.ok) {
        // Recarregar dados
        if (currentReservation) {
          await fetchExistingPayments(currentReservation.id);
          await fetchReservationTotal(currentReservation.id);
        }
        alert('Pagamento excluído com sucesso!');
      } else {
        const errorText = await response.text();
        alert(`Erro ao excluir pagamento: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Erro ao excluir pagamento');
    }
  };

  const handleCheckIn = async (reservationId: number) => {
    try {
      const response = await fetch(`/api/reservations/${reservationId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        body: JSON.stringify({ status: 'checked_in' }),
        credentials: 'include'
      });

      if (response.ok) {
        alert('Check-in realizado com sucesso!');
        onRefresh?.();
        onClose();
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      alert('Erro ao realizar check-in');
    }
  };

  const handleCancelReservation = async (reservationId: number) => {
    if (!confirm('Tem certeza que deseja cancelar esta reserva? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const response = await fetch(`/api/reservations/${reservationId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': document.cookie
        },
        body: JSON.stringify({ status: 'cancelled' }),
        credentials: 'include'
      });

      if (response.ok) {
        // Update room status to available
        await fetch(`/api/rooms/${room.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ status: 'available' }),
        });

        alert('Reserva cancelada com sucesso!');
        onRefresh?.();
        onClose();
      }
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      alert('Erro ao cancelar reserva');
    }
  };

  const generateDocument = async (type: 'voucher' | 'guest-form' | 'receipt', action: 'view' | 'download' | 'print') => {
    if (!currentReservation) return;

    try {
      const response = await fetch(`/api/reservations/${currentReservation.id}/${type}`);
      if (response.ok) {
        const html = await response.text();
        
        if (action === 'view') {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(html);
            newWindow.document.close();
          }
        } else if (action === 'download') {
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${type}-${currentReservation.id}.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (action === 'print') {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(html);
            newWindow.document.close();
            newWindow.print();
          }
        }
      }
    } catch (error) {
      console.error('Erro ao gerar documento:', error);
    }
  };

  const filteredGuests = guests.filter(guest => 
    guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guest.document.includes(searchTerm) ||
    guest.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInventory = inventoryItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(consumptionSearch.toLowerCase()) ||
                         item.category.toLowerCase().includes(consumptionSearch.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory && item.is_active && item.current_stock > 0;
  });

  const inventoryCategories = [...new Set(inventoryItems.map(item => item.category))];
  const totalConsumption = consumptionItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose} />
        
        <div className="inline-block w-full max-w-6xl p-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-2xl rounded-3xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {room.room_number}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {room.room_type}
                </h3>
                <div className="flex items-center space-x-3 mt-1">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isOccupied 
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' 
                      : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  }`}>
                    {isOccupied ? 'Ocupado' : 'Disponível'}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {room.max_occupancy} pessoas
                  </span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8 overflow-x-auto bg-gray-50 dark:bg-gray-900 rounded-xl p-1">
            {!isOccupied ? (
              <button
                onClick={() => setActiveTab('guest')}
                className={`px-6 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === 'guest'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Reservar
              </button>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab('hospedagem')}
                  className={`px-6 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center space-x-2 ${
                    activeTab === 'hospedagem'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  Dados de Hospedagem
                </button>
                <button
                  onClick={() => setActiveTab('consumption')}
                  className={`px-6 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center space-x-2 ${
                    activeTab === 'consumption'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <ShoppingCart className="w-4 h-4 inline mr-2" />
                  Consumo
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`px-6 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center space-x-2 ${
                    activeTab === 'payments'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Pagamentos
                </button>
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`px-6 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center space-x-2 ${
                    activeTab === 'documents'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Documentos
                </button>
              </>
            )}
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px] bg-gray-50 dark:bg-gray-900 rounded-2xl p-8">
            {activeTab === 'guest' && !isOccupied && !currentReservation && (
              <div className="space-y-6">
                {/* Guest Selection */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">Selecionar Hóspede</h4>
                    <button
                      onClick={() => setShowNewGuestForm(!showNewGuestForm)}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Novo Hóspede</span>
                    </button>
                  </div>

                  {showNewGuestForm && (
                    <form onSubmit={handleCreateGuest} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tipo de Hóspede
                        </label>
                        <select
                          value={newGuest.guest_type}
                          onChange={(e) => setNewGuest({ ...newGuest, guest_type: e.target.value as 'individual' | 'corporate' })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="individual">Pessoa Física</option>
                          <option value="corporate">Pessoa Jurídica</option>
                        </select>
                      </div>

                      {newGuest.guest_type === 'individual' ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Nome completo"
                              value={newGuest.name}
                              onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="CPF/RG"
                              value={newGuest.document}
                              onChange={(e) => setNewGuest({ ...newGuest, document: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                            <input
                              type="tel"
                              placeholder="Telefone"
                              value={newGuest.phone || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <input
                              type="email"
                              placeholder="Email"
                              value={newGuest.email || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Endereço"
                            value={newGuest.address || ''}
                            onChange={(e) => setNewGuest({ ...newGuest, address: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Nome da empresa"
                              value={newGuest.company_name || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_name: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="CNPJ"
                              value={newGuest.cnpj || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, cnpj: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                            <input
                              type="tel"
                              placeholder="Telefone"
                              value={newGuest.company_phone || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_phone: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                            <input
                              type="email"
                              placeholder="Email"
                              value={newGuest.company_email || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_email: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              required
                            />
                          </div>
                          <div className="border-t pt-3 mt-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Dados do Funcionário</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <input
                                type="text"
                                placeholder="Nome do funcionário"
                                value={newGuest.name}
                                onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                              />
                              <input
                                type="text"
                                placeholder="CPF do funcionário"
                                value={newGuest.document}
                                onChange={(e) => setNewGuest({ ...newGuest, document: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="flex space-x-2">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                        >
                          {isLoading ? 'Criando...' : 'Criar Hóspede'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewGuestForm(false);
                            setNewGuest({ 
                              name: '', document: '', phone: '', email: '', address: '',
                              guest_type: 'individual', company_name: '', cnpj: '', company_email: '', company_phone: '' 
                            });
                          }}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Buscar hóspede..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {filteredGuests.map((guest) => (
                      <div
                        key={guest.id}
                        onClick={() => setSelectedGuestId(guest.id)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedGuestId === guest.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900 dark:text-white">{guest.name}</p>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                guest.guest_type === 'corporate'
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                              }`}>
                                {guest.guest_type === 'corporate' ? 'PJ' : 'PF'}
                              </span>
                            </div>
                            {guest.guest_type === 'corporate' && guest.company_name && (
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{guest.company_name}</p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {guest.guest_type === 'corporate' && guest.cnpj ? guest.cnpj : guest.document}
                            </p>
                            {guest.email && <p className="text-sm text-gray-600 dark:text-gray-400">{guest.email}</p>}
                          </div>
                          {selectedGuestId === guest.id && (
                            <CheckCircle className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reservation Details */}
                {selectedGuestId && (
                  <div className="border-t pt-6">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Detalhes da Reserva</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Check-in
                        </label>
                        <input
                          type="date"
                          value={reservationData.check_in_date}
                          onChange={(e) => setReservationData({ ...reservationData, check_in_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Check-out
                        </label>
                        <input
                          type="date"
                          value={reservationData.check_out_date}
                          onChange={(e) => setReservationData({ ...reservationData, check_out_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Adultos
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={reservationData.adults}
                          onChange={(e) => setReservationData({ ...reservationData, adults: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Crianças
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={reservationData.children}
                          onChange={(e) => setReservationData({ ...reservationData, children: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Desconto (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={reservationData.discount}
                          onChange={(e) => setReservationData({ ...reservationData, discount: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    {/* Cálculo automático de preços */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-4">
                      <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Cálculo da Diária</h4>
                      <div className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                        <div className="flex justify-between">
                          <span>Base (1 pessoa):</span>
                          <span>R$ {priceBreakdown.baseRate.toFixed(2)}</span>
                        </div>
                        {priceBreakdown.additionalAdults > 0 && (
                          <div className="flex justify-between">
                            <span>Adultos adicionais ({priceBreakdown.additionalAdults}x R$ 40):</span>
                            <span>R$ {(priceBreakdown.additionalAdults * 40).toFixed(2)}</span>
                          </div>
                        )}
                        {priceBreakdown.children > 0 && (
                          <div className="flex justify-between">
                            <span>Crianças ({priceBreakdown.children}x R$ 20):</span>
                            <span>R$ {(priceBreakdown.children * 20).toFixed(2)}</span>
                          </div>
                        )}
                        {priceBreakdown.discount > 0 && (
                          <div className="flex justify-between text-red-600 dark:text-red-400">
                            <span>Desconto:</span>
                            <span>- R$ {priceBreakdown.discount.toFixed(2)}</span>
                          </div>
                        )}
                        <hr className="border-blue-200 dark:border-blue-700" />
                        <div className="flex justify-between font-semibold">
                          <span>Total:</span>
                          <span>R$ {priceBreakdown.finalRate.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Observações
                      </label>
                      <textarea
                        value={reservationData.notes}
                        onChange={(e) => setReservationData({ ...reservationData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'guest' && currentReservation && currentReservation.status === 'confirmed' && (
              <div className="space-y-6">
                {/* Reserved Room - Show Reservation Details */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-indigo-600 text-white p-2 rounded-lg">
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                          Reserva Confirmada
                        </h3>
                        <p className="text-sm text-indigo-600 dark:text-indigo-300">
                          {currentReservation.check_in_date <= new Date().toISOString().split('T')[0] 
                            ? 'Pronto para Check-in' 
                            : `Check-in previsto para ${new Date(currentReservation.check_in_date).toLocaleDateString('pt-BR')}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      {currentReservation.check_in_date <= new Date().toISOString().split('T')[0] && (
                        <button
                          onClick={() => handleCheckIn(currentReservation.id)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Fazer Check-in</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleCancelReservation(currentReservation.id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancelar Reserva</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Hóspede:</span>
                        <p className="font-medium text-indigo-900 dark:text-indigo-100">{currentReservation.guest_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Check-in:</span>
                        <p className="font-medium text-indigo-900 dark:text-indigo-100">
                          {new Date(currentReservation.check_in_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Hóspedes:</span>
                        <p className="font-medium text-indigo-900 dark:text-indigo-100">
                          {currentReservation.adults} adultos, {currentReservation.children} crianças
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Check-out:</span>
                        <p className="font-medium text-indigo-900 dark:text-indigo-100">
                          {new Date(currentReservation.check_out_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Valor Total:</span>
                        <p className="font-medium text-indigo-900 dark:text-indigo-100">
                          R$ {currentReservation.total_amount.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-indigo-600 dark:text-indigo-400">Status:</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 ml-2">
                          Reservado
                        </span>
                      </div>
                    </div>
                  </div>

                  {currentReservation.notes && (
                    <div className="mt-4 p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <span className="text-sm font-medium text-indigo-900 dark:text-indigo-100">Observações:</span>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">{currentReservation.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'hospedagem' && isOccupied && currentReservation && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">Informações da Reserva</h4>
                    {currentReservation.created_by_name && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                        Criado por: {currentReservation.created_by_name}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Hóspede:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{currentReservation.guest_name}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Check-in:</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {new Date(currentReservation.check_in_date).toLocaleDateString('pt-BR')}
                          {currentReservation.check_in_time && (
                            <span className="text-green-600 dark:text-green-400 ml-2">
                              (Realizado às {new Date(currentReservation.check_in_time).toLocaleTimeString('pt-BR')})
                            </span>
                          )}
                        </p>
                        {currentReservation.updated_by_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Check-in realizado por: <span className="font-medium ml-1">{currentReservation.updated_by_name}</span>
                          </p>
                        )}
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Hóspedes:</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {currentReservation.adults} adultos, {currentReservation.children} crianças
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Check-out:</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {new Date(currentReservation.check_out_date).toLocaleDateString('pt-BR')}
                          {currentReservation.check_out_time && (
                            <span className="text-blue-600 dark:text-blue-400 ml-2">
                              (Realizado às {new Date(currentReservation.check_out_time).toLocaleTimeString('pt-BR')})
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Status do pagamento:</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${
                          currentReservation.payment_status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                          currentReservation.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {currentReservation.payment_status === 'paid' ? 'Pago' :
                           currentReservation.payment_status === 'partial' ? 'Parcial' : 'Pendente'}
                        </span>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-6 rounded-xl border border-blue-200 dark:border-gray-600 shadow-lg">
                        <div className="flex items-center justify-center mb-4">
                          <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full mr-3">
                            <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h5 className="text-xl font-bold text-gray-800 dark:text-white">Resumo Financeiro</h5>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Diária</div>
                            <div className="text-lg font-bold text-green-600 dark:text-green-400">
                              {reservationTotal ? 
                                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reservationTotal.daily_rate || 0) :
                                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currentReservation.daily_rate || 0)
                              }
                            </div>
                          </div>
                          
                          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Consumos</div>
                            <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                              {reservationTotal ? 
                                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reservationTotal.consumptions_total || 0) :
                                'R$ 0,00'
                              }
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 p-4 rounded-lg text-white mb-3 shadow-md">
                          <div className="flex justify-between items-center">
                            <span className="text-sm opacity-90">Total Geral</span>
                            <span className="text-2xl font-bold">
                              {reservationTotal ? 
                                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reservationTotal.total_amount || 0) :
                                new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currentReservation.total_amount || 0)
                              }
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="text-xs text-green-600 dark:text-green-400 mb-1">Pago</div>
                            <div className="text-lg font-bold text-green-700 dark:text-green-300">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalPaid)}
                            </div>
                          </div>
                          
                          {remainingBalance > 0 ? (
                            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
                              <div className="text-xs text-red-600 dark:text-red-400 mb-1">Saldo</div>
                              <div className="text-lg font-bold text-red-700 dark:text-red-300">
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(remainingBalance)}
                              </div>
                            </div>
                          ) : totalPaid > 0 ? (
                            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800 flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                              <span className="text-green-700 dark:text-green-300 font-bold">Quitado</span>
                            </div>
                          ) : (
                            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Saldo</div>
                              <div className="text-lg font-bold text-gray-600 dark:text-gray-400">R$ 0,00</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* New action buttons for checkout date change and room reallocation */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <button
                    onClick={() => {
                      setNewCheckoutDate(currentReservation?.check_out_date || '');
                      setShowCheckoutDateForm(true);
                    }}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center space-x-2 transition-all"
                  >
                    <Calendar className="w-5 h-5" />
                    <span>Alterar Check-out</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowRoomReallocationForm(true);
                      fetchAvailableRooms();
                    }}
                    className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center space-x-2 transition-all"
                  >
                    <Users className="w-5 h-5" />
                    <span>Realocar Quarto</span>
                  </button>

                  <button
                    onClick={handleCheckOut}
                    disabled={isLoading || remainingBalance > 0}
                    className={`px-4 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-all ${
                      remainingBalance > 0 
                        ? 'bg-gray-400 cursor-not-allowed text-white' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    } ${isLoading ? 'opacity-50' : ''}`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>{isLoading ? 'Processando...' : 'Check-out'}</span>
                  </button>
                </div>

                {/* Checkout date change form */}
                {showCheckoutDateForm && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <h4 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-3">
                      Alterar Data de Check-out
                    </h4>
                    <div className="flex items-center space-x-3">
                      <input
                        type="date"
                        value={newCheckoutDate}
                        onChange={(e) => setNewCheckoutDate(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        onClick={handleChangeCheckoutDate}
                        disabled={!newCheckoutDate || isLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium"
                      >
                        Confirmar
                      </button>
                      <button
                        onClick={() => setShowCheckoutDateForm(false)}
                        className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {/* Room reallocation form */}
                {showRoomReallocationForm && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                    <h4 className="text-lg font-medium text-purple-900 dark:text-purple-100 mb-3">
                      Realocar para Outro Quarto
                    </h4>
                    <div className="space-y-3">
                      <select
                        value={selectedNewRoomId || ''}
                        onChange={(e) => setSelectedNewRoomId(Number(e.target.value) || null)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Selecione um quarto disponível</option>
                        {availableRooms.map(room => (
                          <option key={room.id} value={room.id}>
                            QUARTO {room.room_number}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={handleRoomReallocation}
                          disabled={!selectedNewRoomId || isLoading}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg font-medium"
                        >
                          Confirmar Realocação
                        </button>
                        <button
                          onClick={() => {
                            setShowRoomReallocationForm(false);
                            setSelectedNewRoomId(null);
                          }}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'consumption' && isOccupied && (
              <div className="space-y-6">
                {/* Custom Item Form */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-amber-900 dark:text-amber-100 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Adicional Personalizado
                    </h4>
                    <button
                      onClick={() => setShowCustomItemForm(!showCustomItemForm)}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Adicionar</span>
                    </button>
                  </div>
                  
                  {showCustomItemForm && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Descrição do adicional"
                        value={customItem.name}
                        onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                        className="px-3 py-2 border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Valor (R$)"
                          value={customItem.price || ''}
                          onChange={(e) => setCustomItem({ ...customItem, price: parseFloat(e.target.value) || 0 })}
                          className="flex-1 px-3 py-2 border border-amber-300 dark:border-amber-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <button
                          onClick={addCustomItem}
                          disabled={!customItem.name || customItem.price <= 0}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Adicionar
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inventory Browser */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">Catálogo de Produtos</h4>
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Buscar produtos..."
                          value={consumptionSearch}
                          onChange={(e) => setConsumptionSearch(e.target.value)}
                          className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                      </div>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="all">Todas as Categorias</option>
                        {inventoryCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-60 overflow-y-auto">
                    {filteredInventory.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => addConsumptionItem(item)}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {item.name}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{item.category}</p>
                          </div>
                          <Plus className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="font-bold text-blue-600 dark:text-blue-400">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.sale_price)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Estoque: {item.current_stock}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Consumption Cart */}
                {consumptionItems.length > 0 && (
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Carrinho ({consumptionItems.length} {consumptionItems.length === 1 ? 'item' : 'itens'})
                      </h4>
                      <button
                        onClick={clearConsumptionCart}
                        className="text-red-600 hover:text-red-700 text-sm flex items-center space-x-1"
                      >
                        <X className="w-4 h-4" />
                        <span>Limpar</span>
                      </button>
                    </div>
                    
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {consumptionItems.map((consumption, index) => {
                        const itemKey = consumption.item_id || consumption.item_name || index;
                        return (
                          <div key={itemKey} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-900 dark:text-white">{consumption.name}</p>
                                {consumption.is_custom && (
                                  <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400 px-2 py-0.5 rounded-full text-xs">
                                    Adicional
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {consumption.category} • {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(consumption.unit_price)} cada
                              </p>
                            </div>
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={() => updateConsumptionQuantity(itemKey, consumption.quantity - 1)}
                                className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-12 text-center font-medium text-gray-900 dark:text-white">
                                {consumption.quantity}
                              </span>
                              <button
                                onClick={() => updateConsumptionQuantity(itemKey, consumption.quantity + 1)}
                                className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <div className="w-24 text-right">
                                <p className="font-bold text-gray-900 dark:text-white">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(consumption.quantity * consumption.unit_price)}
                                </p>
                              </div>
                              <button
                                onClick={() => removeConsumptionItem(itemKey)}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 flex justify-between items-center">
                      <div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalConsumption)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {consumptionItems.reduce((sum, item) => sum + item.quantity, 0)} {consumptionItems.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'item' : 'itens'} no carrinho
                        </p>
                      </div>
                      <button
                        onClick={handleSaveConsumption}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 flex items-center space-x-2"
                      >
                        <Package className="w-5 h-5" />
                        <span>{isLoading ? 'Salvando...' : 'Adicionar ao Consumo'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Consumos Registrados */}
                {existingConsumptions.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-900 dark:text-green-100 mb-4 flex items-center">
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Consumos Registrados
                    </h4>
                    <div className="space-y-3">
                      {existingConsumptions.map((consumption: any, index: number) => (
                        <div key={consumption.id || index} className="bg-gradient-to-r from-white to-green-50 dark:from-gray-800 dark:to-green-900/10 p-4 rounded-xl border border-green-200 dark:border-green-800/30 shadow-md hover:shadow-lg transition-all duration-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="flex-shrink-0">
                                  {consumption.item_id === null ? (
                                    <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-2 rounded-lg shadow-sm">
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-2 rounded-lg shadow-sm">
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-gray-900 dark:text-white text-lg leading-tight">
                                    {consumption.name || consumption.item_name}
                                  </h4>
                                  <div className="flex items-center space-x-2 mt-1">
                                    {consumption.item_id === null ? (
                                      <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                                        ✨ Adicional
                                      </span>
                                    ) : (
                                      <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                                        📦 Estoque
                                      </span>
                                    )}
                                    {consumption.category && (
                                      <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full text-xs">
                                        {consumption.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 mb-3">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div className="flex items-center">
                                    <div className="bg-blue-100 dark:bg-blue-900/30 p-1 rounded mr-2">
                                      <svg className="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-600 dark:text-gray-400">Qtd: </span>
                                    <span className="font-bold text-gray-900 dark:text-white">{consumption.quantity}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <div className="bg-green-100 dark:bg-green-900/30 p-1 rounded mr-2">
                                      <DollarSign className="w-3 h-3 text-green-600 dark:text-green-400" />
                                    </div>
                                    <span className="text-gray-600 dark:text-gray-400">Unit: </span>
                                    <span className="font-bold text-gray-900 dark:text-white">R$ {consumption.unit_price.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {new Date(consumption.created_at).toLocaleDateString('pt-BR')} às {new Date(consumption.created_at).toLocaleTimeString('pt-BR')}
                                  {consumption.created_by_name && (
                                    <>
                                      <svg className="w-3 h-3 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      <span className="font-medium">{consumption.created_by_name}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end space-y-3 ml-4">
                              <div className="text-right">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg shadow-md">
                                  <div className="text-xs opacity-90">Total</div>
                                  <div className="text-xl font-bold">
                                    R$ {consumption.total_amount.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleDeleteConsumption(consumption.id)}
                                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2 rounded-lg text-sm flex items-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200"
                                title="Excluir consumo"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Excluir</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-green-200 dark:border-green-700 pt-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium text-green-900 dark:text-green-100">
                          Total de Consumos:
                        </span>
                        <span className="text-xl font-bold text-green-600 dark:text-green-400">
                          R$ {existingConsumptions.reduce((sum: number, item: any) => sum + item.total_amount, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'payments' && isOccupied && currentReservation && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">Pagamentos</h4>
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Novo Pagamento</span>
                  </button>
                </div>

                {showPaymentForm && (
                  <form onSubmit={handlePayment} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg space-y-4">
                    <h5 className="font-medium text-gray-900 dark:text-white">Registrar Pagamento</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Valor (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={paymentData.amount}
                          onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Forma de Pagamento
                        </label>
                        <select
                          value={paymentData.payment_method}
                          onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        >
                          <option value="cash">Dinheiro</option>
                          <option value="credit_card">Cartão de Crédito</option>
                          <option value="debit_card">Cartão de Débito</option>
                          <option value="pix">PIX</option>
                          <option value="bank_transfer">Transferência Bancária</option>
                          <option value="courtesy">🎁 Cortesia</option>
                          <option value="billing">📄 Faturamento</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Observações
                      </label>
                      <textarea
                        value={paymentData.notes}
                        onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                      >
                        {isLoading ? 'Processando...' : 'Registrar Pagamento'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPaymentForm(false)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}

                {/* Pagamentos Registrados */}
                {existingPayments.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pagamentos Registrados
                    </h4>
                    <div className="space-y-3">
                      {existingPayments.map((payment: any, index: number) => (
                        <div key={payment.id || index} className="bg-gradient-to-r from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/10 p-4 rounded-xl border border-blue-200 dark:border-blue-800/30 shadow-md hover:shadow-lg transition-all duration-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <div className="flex-shrink-0">
                                  <div className={`p-2 rounded-lg shadow-sm ${
                                    payment.payment_method === 'cash' ? 'bg-gradient-to-br from-green-400 to-green-500' :
                                    payment.payment_method === 'credit_card' ? 'bg-gradient-to-br from-purple-400 to-purple-500' :
                                    payment.payment_method === 'debit_card' ? 'bg-gradient-to-br from-blue-400 to-blue-500' :
                                    payment.payment_method === 'pix' ? 'bg-gradient-to-br from-orange-400 to-orange-500' :
                                    payment.payment_method === 'bank_transfer' ? 'bg-gradient-to-br from-indigo-400 to-indigo-500' :
                                    payment.payment_method === 'courtesy' ? 'bg-gradient-to-br from-pink-400 to-pink-500' :
                                    'bg-gradient-to-br from-gray-400 to-gray-500'
                                  }`}>
                                    {payment.payment_method === 'cash' && (
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                    {(payment.payment_method === 'credit_card' || payment.payment_method === 'debit_card') && (
                                      <CreditCard className="w-5 h-5 text-white" />
                                    )}
                                    {payment.payment_method === 'pix' && (
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                    {payment.payment_method === 'bank_transfer' && (
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                      </svg>
                                    )}
                                    {payment.payment_method === 'courtesy' && (
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                      </svg>
                                    )}
                                    {payment.payment_method === 'billing' && (
                                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-gray-900 dark:text-white text-lg leading-tight">
                                    {payment.payment_method === 'cash' && 'Dinheiro'}
                                    {payment.payment_method === 'credit_card' && 'Cartão de Crédito'}
                                    {payment.payment_method === 'debit_card' && 'Cartão de Débito'}
                                    {payment.payment_method === 'pix' && 'PIX'}
                                    {payment.payment_method === 'bank_transfer' && 'Transferência Bancária'}
                                    {payment.payment_method === 'courtesy' && 'Cortesia'}
                                    {payment.payment_method === 'billing' && 'Faturamento'}
                                  </h4>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm text-white ${
                                      payment.payment_method === 'cash' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                      payment.payment_method === 'credit_card' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                                      payment.payment_method === 'debit_card' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                      payment.payment_method === 'pix' ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                      payment.payment_method === 'bank_transfer' ? 'bg-gradient-to-r from-indigo-500 to-indigo-600' :
                                      payment.payment_method === 'courtesy' ? 'bg-gradient-to-r from-pink-500 to-pink-600' :
                                      'bg-gradient-to-r from-gray-500 to-gray-600'
                                    }`}>
                                      💰 Pagamento
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 mb-3">
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {new Date(payment.created_at).toLocaleDateString('pt-BR')} às {new Date(payment.created_at).toLocaleTimeString('pt-BR')}
                                  {payment.created_by_name && (
                                    <>
                                      <svg className="w-3 h-3 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      <span className="font-medium">{payment.created_by_name}</span>
                                    </>
                                  )}
                                </div>
                                {payment.notes && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                                    <span className="font-medium">Obs:</span> {payment.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end space-y-3 ml-4">
                              <div className="text-right">
                                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-lg shadow-md">
                                  <div className="text-xs opacity-90">Valor</div>
                                  <div className="text-xl font-bold">
                                    R$ {payment.amount.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {/* TODO: Implementar edição */}}
                                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                                  title="Editar pagamento"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeletePayment(payment.id)}
                                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                                  title="Excluir pagamento"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-blue-200 dark:border-blue-700 pt-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium text-blue-900 dark:text-blue-100">
                          Total Pago:
                        </span>
                        <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          R$ {totalPaid.toFixed(2)}
                        </span>
                      </div>
                      {remainingBalance > 0 && (
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-lg font-medium text-red-900 dark:text-red-100">
                            Saldo Devedor:
                          </span>
                          <span className="text-xl font-bold text-red-600 dark:text-red-400">
                            R$ {remainingBalance.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {remainingBalance === 0 && totalPaid > 0 && (
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-lg font-medium text-green-900 dark:text-green-100">
                            ✅ Pagamento Completo
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            )}

            {activeTab === 'documents' && isOccupied && (
              <div className="space-y-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Documentos</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { type: 'voucher' as const, title: 'Voucher', description: 'Comprovante de hospedagem', color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' },
                    { type: 'guest-form' as const, title: 'Ficha do Hóspede', description: 'Dados do hóspede', color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
                    { type: 'receipt' as const, title: 'Recibo', description: 'Comprovante de pagamento', color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' }
                  ].map((doc) => (
                    <div key={doc.type} className={`p-6 border rounded-lg ${doc.color}`}>
                      <h5 className="font-medium text-gray-900 dark:text-white mb-1">{doc.title}</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{doc.description}</p>
                      <div className="space-y-2">
                        <button
                          onClick={() => generateDocument(doc.type, 'view')}
                          className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                          <FileText className="w-4 h-4" />
                          <span>Visualizar</span>
                        </button>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => generateDocument(doc.type, 'download')}
                            className="flex-1 flex items-center justify-center space-x-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            <Download className="w-3 h-3" />
                            <span>Baixar</span>
                          </button>
                          <button
                            onClick={() => generateDocument(doc.type, 'print')}
                            className="flex-1 flex items-center justify-center space-x-1 px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            <Printer className="w-3 h-3" />
                            <span>Imprimir</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Fechar
            </button>
            {activeTab === 'guest' && selectedGuestId && (
              <button
                onClick={handleCreateReservation}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Criando...' : 'Criar Reserva'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
